import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import {
  getLastAttemptedSignInMethod,
  setLastAttemptedSignInMethod,
  setLastSuccessfulSignInMethod,
} from 'src/components/Account/lastSignInMethod'
import {
  deleteReferralCodeFromLocalStorage,
  getReferralCodeFromLocalStorage,
  setReferralCodeToLocalStorage,
} from 'src/components/UserReferral/utils'
import { openNewUserOnboardingDialog } from 'src/features/NewUserOnboarding'
import { clearSearchHistory } from 'src/hooks/useSearchHistory'
import { executeRefreshToken, useMyInfo } from 'src/services/apollo/auth'
import { baseApolloClient } from 'src/services/apollo/client'
import { refetchGetAllPaymentItems } from 'src/services/apollo/payment'
import { executeSetPreferences } from 'src/services/apollo/preference'
import { executeSetReferrer } from 'src/services/apollo/referral'
import { restartWsConnection } from 'src/services/apollo/wsLink'
import { baseClient } from 'src/services/client'
import { resetLocalPreferences } from '../services/helpers/preference'
import { getApiUrl } from './config'
import { experimentLogout } from './experimentNew'
import { unsetIntercomUserJwt } from './intercom/storage'
import { useQs } from './qs'
import { refreshPushTokenEffect, withdrawPushToken } from './swClient'
import { getMayTokenBeValid, setToken, useProactiveTokenRefresh } from './token'
import { track, trackerLogout } from './tracker'

export const addRedirectState = (url: string, redirectUrl: string) => {
  const state = encodeURIComponent(
    `redirect=${encodeURIComponent(redirectUrl)}`,
  )
  const u = new URL(url)
  const referralCode = getReferralCodeFromLocalStorage()

  u.searchParams.append('state', state)
  if (referralCode) u.searchParams.append('referralCode', referralCode)
  return u.toString()
}

export const redirectToOAuth = (
  type: string,
  target: string = window.location.href,
) => {
  const url = addRedirectState(`${getApiUrl()}/auth/${type}`, target)
  setLastAttemptedSignInMethod(type)
  window.location.href = url
}

export const useInitialQueryStringHandler = () => {
  const [{ token, isNew, submitTask, refCode }, setQs] = useQs()
  const handledOnboarding = useRef(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (token) {
      setToken(token)

      // OAuth completes via a full-page redirect, so the click that started it
      // is in a previous SPA session. We persist the attempted provider before
      // the redirect (see redirectToOAuth) and promote it here on confirmed
      // success. Email logins resolve in-band and write last-successful directly,
      // so guard against a stale 'email' attempted value being mis-attributed.
      const attempted = getLastAttemptedSignInMethod()
      const oauthProvider =
        attempted && attempted !== 'email' ? attempted : undefined
      if (oauthProvider) {
        setLastSuccessfulSignInMethod(oauthProvider)
      }

      track(isNew === 'true' ? 'register_success' : 'login_success', {
        type: 'token',
        ...(oauthProvider && { signInMethod: oauthProvider }),
      })

      // Remove refCode since it shouldn't exist for registered user
      const fieldsToRemove: string[] = ['refCode']
      fieldsToRemove.push('token')
      baseApolloClient.resetStore()

      if (isNew === 'true') {
        // If not from gen page, open register onboarding dialog on sign up completes
        if (!Number(submitTask)) {
          if (!handledOnboarding.current) {
            openNewUserOnboardingDialog({ navigate }).catch(() => {})
          }
          handledOnboarding.current = true
        }
      }

      // Remove query string if not from task submission
      if (!Number(submitTask)) {
        fieldsToRemove.push('isNew')
      }

      /** Updating query string once only to avoid race conditions */
      setQs(prev => ({
        ...prev,
        ...Object.fromEntries(fieldsToRemove.map(f => [f, null])),
      }))
    }
  }, [token])

  // Wait for previous effect to complete
  useEffect(() => {
    if (!token && refCode) {
      setReferralCodeToLocalStorage(refCode)
      // Consume the refCode
      setQs(prev => ({
        ...prev,
        refCode: null,
      }))
    }
  }, [refCode, token])

  useEffect(() => {
    if (getMayTokenBeValid()) executeRefreshToken({})
  }, [])
}

export async function logoutUser() {
  withdrawPushToken('logout')
  await baseClient.post('/v1/auth/logout', {})
  setToken('')
  restartWsConnection()
  await baseApolloClient.resetStore()
  refetchGetAllPaymentItems()
  trackerLogout()
  experimentLogout()
  unsetIntercomUserJwt()
  resetLocalPreferences()
  clearSearchHistory()
}

/**
 * Sets user preferences and referral code
 * After user registering by email completes
 *
 * Should NOT be called in OAuth registration process
 *
 * @param args
 * @returns
 */
export function getPostRegistrationRequests(args: {
  promoEmailPref?: boolean
}) {
  const referralCodeFromLocalStorage = getReferralCodeFromLocalStorage()

  const refCode = referralCodeFromLocalStorage

  return [
    args.promoEmailPref &&
      executeSetPreferences({
        value: {
          promoEmail: true,
        },
      }),
    /* Ref Code can be invalid. Should not block registration process. */
    refCode &&
      void executeSetReferrer({ code: refCode }).catch(() => {
        // Delete if referrer code is rejected by server.
        deleteReferralCodeFromLocalStorage()
      }),
  ].filter(Boolean)
}

let lastWsUserId: string | undefined
function resubscribeWs(userId?: string) {
  if (userId === lastWsUserId) return
  lastWsUserId = userId
  restartWsConnection()
}

export function UserInfoChangeHandler() {
  const { data } = useMyInfo()

  useEffect(() => {
    if (data?.id) {
      refreshPushTokenEffect(data.id)
    }
    resubscribeWs(data?.id)
  }, [data?.id])

  // Proactively refresh token before expiration to prevent expired token usage
  const handleRefreshToken = useCallback(() => executeRefreshToken({}), [])
  useProactiveTokenRefresh(handleRefreshToken)

  return null
}
